import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { anonApi } from "../anonymizerApi";

// The anonymizer API (smile-anonymizer-api) is OIDC-protected with roles;
// anonApi attaches the bearer token from the logged-in user.
const apiCall = (path, options) => anonApi(path, options);

export const normalizeConnector = (c) => c || "remedy";

// per-connector field metadata endpoint (remedy keeps the original,
// server-side-cached endpoint; other connectors go through /v1/connectors)
async function fetchFormFields(form, connector) {
    if (normalizeConnector(connector) === "remedy") {
        const result = await apiCall(`/v1/config/forms/${encodeURIComponent(form)}/fields`, { method: "GET" });
        return result.data || [];
    }
    const result = await apiCall(
        `/v1/connectors/${encodeURIComponent(connector)}/collections/${encodeURIComponent(form)}/fields`,
        { method: "GET" }
    );
    return result.data || [];
}

// loads field metadata for every {form, connector} referenced by the config
async function loadFormFields(specs, knownFields) {
    const added = [];
    const key = (s) => `${normalizeConnector(s.connector)}|${s.form}`;
    const seen = new Set();
    const wanted = (specs || []).filter((s) => {
        if (!s || !s.form || seen.has(key(s))) return false;
        seen.add(key(s));
        return !knownFields.find((f) => f.form === s.form && normalizeConnector(f.connector) === normalizeConnector(s.connector));
    });
    await Promise.all(
        wanted.map(async (s) => {
            try {
                const fields = await fetchFormFields(s.form, s.connector);
                added.push({ form: s.form, connector: normalizeConnector(s.connector), fields: fields });
            } catch (e) {
                // form without metadata - editors fall back to no completion
            }
        })
    );
    return added;
}

export function collectForms(config) {
    const specs = [];
    (config || []).forEach((block) => {
        if (!block) return;
        (block.personConfig || []).forEach((pc) => {
            specs.push({ form: pc.form, connector: pc.connector });
            (pc.relatedRecords || []).forEach((rr) =>
                specs.push({ form: rr.form, connector: rr.connector || pc.connector }));
        });
        (block.textConfig || []).forEach((tc) => specs.push({ form: tc.form, connector: tc.connector }));
        (block.deleteConfig || []).forEach((dc) => specs.push({ form: dc.form, connector: dc.connector }));
        if (block.anonymizeText && block.anonymizeText.entries) {
            block.anonymizeText.entries.forEach((e) => specs.push({ form: e.form, connector: e.connector }));
        }
    });
    return specs;
}

export const getAnonymizerConfig = createAsyncThunk("anonymizer/getConfig", async (_, thunkAPI) => {
    const config = await apiCall("/v1/config", { method: "GET" });
    const fields = await loadFormFields(collectForms(config), thunkAPI.getState().anonymizer.fields);
    return { config: config, fields: fields };
});

export const saveAnonymizerConfig = createAsyncThunk("anonymizer/saveConfig", async (_, thunkAPI) => {
    const config = thunkAPI.getState().anonymizer.config;
    const saved = await apiCall("/v1/config", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(config)
    });
    const fields = await loadFormFields(collectForms(saved), thunkAPI.getState().anonymizer.fields);
    return { config: saved, fields: fields };
});

export const getAnonymizerForms = createAsyncThunk("anonymizer/getForms", async () => {
    const result = await apiCall("/v1/config/forms", { method: "GET" });
    return (result.data || []).map((f) => (f && f.name ? f.name : f)).filter(Boolean);
});

export const loadFieldsForForm = createAsyncThunk("anonymizer/loadFieldsForForm", async (spec, thunkAPI) => {
    // spec: {form, connector} - a plain string means a remedy form (legacy callers)
    const normalized = typeof spec === "string" ? { form: spec } : spec;
    const added = await loadFormFields([normalized], thunkAPI.getState().anonymizer.fields);
    return added;
});

export const getAnonymizerConnectors = createAsyncThunk("anonymizer/getConnectors", async () => {
    return await apiCall("/v1/connectors", { method: "GET" });
});

export const getConnectorForms = createAsyncThunk("anonymizer/getConnectorForms", async (connector) => {
    const result = await apiCall(`/v1/connectors/${encodeURIComponent(connector)}/collections`, { method: "GET" });
    return { connector: connector, forms: result.data || [] };
});

const anonymizerSlice = createSlice({
    name: "anonymizer",
    initialState: {
        config: [],
        fields: [],
        forms: [],
        formsByConnector: {},
        connectors: [],
        loading: false,
        unsaved: false,
        codeView: false,
        error: null
    },
    reducers: {
        updateAnonymizerConfig: (state, action) => {
            state.config = action.payload.config;
            state.unsaved = true;
        },
        setAnonymizerCodeView: (state, action) => {
            state.codeView = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAnonymizerConfig.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAnonymizerConfig.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(getAnonymizerConfig.fulfilled, (state, action) => {
                state.loading = false;
                state.config = action.payload.config;
                state.fields = [...state.fields, ...action.payload.fields];
            })
            .addCase(saveAnonymizerConfig.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveAnonymizerConfig.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(saveAnonymizerConfig.fulfilled, (state, action) => {
                state.loading = false;
                state.unsaved = false;
                state.config = action.payload.config;
                state.fields = [...state.fields, ...action.payload.fields];
            })
            .addCase(getAnonymizerForms.fulfilled, (state, action) => {
                state.forms = action.payload;
            })
            .addCase(loadFieldsForForm.fulfilled, (state, action) => {
                const newOnes = action.payload.filter((nf) => !state.fields.find(
                    (f) => f.form === nf.form && normalizeConnector(f.connector) === normalizeConnector(nf.connector)
                ));
                state.fields = [...state.fields, ...newOnes];
            })
            .addCase(getAnonymizerConnectors.fulfilled, (state, action) => {
                state.connectors = action.payload || [];
            })
            .addCase(getConnectorForms.fulfilled, (state, action) => {
                state.formsByConnector = {
                    ...state.formsByConnector,
                    [action.payload.connector]: action.payload.forms
                };
            });
    }
});

export const { updateAnonymizerConfig, setAnonymizerCodeView } = anonymizerSlice.actions;

export default anonymizerSlice.reducer;
